import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection, query, where, onSnapshot, getDocs, getDoc,
  updateDoc, deleteDoc, doc, orderBy
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useUserProfile } from '../contexts/UserProfileContext';
import MoveNoteModal from '../components/MoveNoteModal';
import jsPDF from 'jspdf';
import Header from '../components/Header';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, ChevronDown, Edit, FileDown, ArrowRightLeft, ChevronsUpDown, Trash2, AlertTriangle, ArrowLeft 
} from 'lucide-react';
import lumosyeLogo from '../assets/Lumosye-logo.png';
import lumochartLogo from '../assets/lumochart-login.png';

// --- Reusable Form Components for Consistent Styling ---
const FormInput = ({ ...props }) => (
    <div className="relative w-full">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
      <input 
        {...props}
        className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg pl-10 pr-4 py-2.5 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" 
      />
    </div>
);

const FormSelect = ({ children, ...props }) => (
  <div className="relative w-full">
    <select 
      {...props}
      className="appearance-none w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-2.5 text-base text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    >
      {children}
    </select>
    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
  </div>
);

// --- NOTE: Moved noteSectionOrder to the top-level scope ---
const noteSectionOrder = [
    { key: 'summary', title: 'Summary' }, { key: 'hpi', title: 'History of Present Illness' }, 
    { key: 'subjective', title: 'Subjective' }, { key: 'pastMedicalHistory', title: 'Past Medical History' }, 
    { key: 'pastSurgicalHistory', title: 'Past Surgical History' }, { key: 'familyHistory', title: 'Family History' }, 
    { key: 'socialHistory', title: 'Social History' }, { key: 'medications', title: 'Medications' }, 
    { key: 'allergies', title: 'Allergies' }, { key: 'ros', title: 'Review of Systems' }, 
    { key: 'exam', title: 'Physical Exam' }, { key: 'objective', title: 'Objective' }, 
    { key: 'ap', title: 'Assessment & Plan' }, { key: 'mdm', title: 'Medical Decision Making' },
];

// --- Redesigned Note Card Component ---
const NoteCard = ({ note, isExpanded, onExpand, onEdit, onExport, onMove, onDelete }) => {
    const noteTypeStyles = {
        Admission: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300",
        Progress: "bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300",
        "Hospital Course": "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300",
        Discharge: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
        "ED Note": "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
        "Initial Consult": "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300",
        "Follow-up Consult": "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-300",
        "Clinic Visit": "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300",
        "Clinic Follow-Up": "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300",
        Default: "bg-gray-100 text-gray-800 dark:bg-zinc-700 dark:text-zinc-300",
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "No date";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
        return date.toLocaleString();
    };

    const getPreview = (note) => {
        if (note.summary) return note.summary.slice(0, 120) + (note.summary.length > 120 ? '...' : '');
        const text = note.subjective || note.hpi || '';
        return text ? text.slice(0, 120) + (text.length > 120 ? '...' : '') : <span className="text-gray-400 italic">No preview available</span>;
    };

    return (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-white dark:bg-zinc-900">
                <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${noteTypeStyles[note.type] || noteTypeStyles.Default}`}>
                                {note.type || 'Note'}
                            </span>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{formatDate(note.timestamp)}</p>
                        </div>
                        <button onClick={onExpand} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-500">
                            <motion.div animate={{ rotate: isExpanded ? 180 : 0 }}><ChevronsUpDown size={18} /></motion.div>
                        </button>
                    </div>
                    {!isExpanded && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">{getPreview(note)}</p>
                    )}
                </div>
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                            <div className="px-6 pb-6 space-y-4 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                                {noteSectionOrder.map(({ key, title }) => {
                                    if (note[key]) {
                                        return (
                                            <div key={key}>
                                                <strong className="font-semibold uppercase text-gray-500 dark:text-gray-400">{title}:</strong>
                                                <p>{note[key]}</p>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <div className="bg-slate-50 dark:bg-zinc-800/50 px-6 py-3 flex justify-end items-center gap-2">
                <button onClick={() => onMove(note.id)} title="Move Note" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-500"><ArrowRightLeft size={16} /></button>
                <button onClick={() => onExport(note)} title="Export as PDF" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-500"><FileDown size={16} /></button>
                <button onClick={() => onEdit(note)} title="Edit Note" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-500"><Edit size={16} /></button>
                <button onClick={() => onDelete(note)} title="Delete Note" className="p-2 rounded-full text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50"><Trash2 size={16} /></button>
            </div>
        </div>
    );
};

// --- Main Page Component ---
export default function PastNotes() {
  const { id: patientId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { profile } = useUserProfile();
  const [allPatientNotes, setAllPatientNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [noteTypeFilter, setNoteTypeFilter] = useState('');
  const [expandedNoteId, setExpandedNoteId] = useState(null);

  useEffect(() => {
    if (!currentUser || !patientId) return;
    const q = query(collection(db, 'notes'), where('ownerId', '==', currentUser.uid), where('patientID', '==', patientId), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllPatientNotes(data);
    });
    return () => unsubscribe();
  }, [currentUser, patientId]);

  useEffect(() => {
    async function fetchPatients() {
      if (!currentUser) return;
      const q = query(collection(db, 'patients'), where('ownerId', '==', currentUser.uid));
      const snapshot = await getDocs(q);
      setPatients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }
    fetchPatients();
  }, [currentUser]);

  // --- FILTER FIX: A single useEffect to handle all filtering ---
  useEffect(() => {
    let notesToFilter = [...allPatientNotes];

    if (noteTypeFilter) {
      notesToFilter = notesToFilter.filter(n => n.type === noteTypeFilter);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      notesToFilter = notesToFilter.filter(n =>
        Object.values(n).some(val => 
          typeof val === 'string' && val.toLowerCase().includes(q)
        )
      );
    }
    
    setFilteredNotes(notesToFilter);
  }, [allPatientNotes, noteTypeFilter, searchQuery]);


  const handleMoveNote = async (noteId, newPatientId) => {
    await updateDoc(doc(db, 'notes', noteId), { patientID: newPatientId });
    setShowMoveModal(false);
    setSelectedNoteId(null);
  };
  
  const handleDeleteNote = async () => {
    if (!noteToDelete) return;
    await deleteDoc(doc(db, 'notes', noteToDelete.id));
    setNoteToDelete(null);
  };

  const handleEditNote = (note) => {
    const type = note.type || '';
    const patientID = note.patientID || patientId;
    const noteID = note.id;

    switch (type) {
        case 'Admission': navigate(`/admission-note/${patientID}/${noteID}`); break;
        case 'Progress': navigate(`/progress-note/${patientID}/${noteID}`); break;
        case 'Hospital Course': navigate(`/hospital-course/${patientID}`); break;
        case 'Discharge': navigate(`/discharge-summary/${patientID}`); break;
        case 'Clinic Visit': navigate(`/new-visit-note/${patientID}/${noteID}`); break;
        case 'Clinic Follow-Up': navigate(`/follow-up-visit-note/${patientID}/${noteID}`); break;
        case 'ED Note': navigate(`/ed-note/${patientID}/${noteID}`); break;
        case 'Initial Consult': navigate(`/initial-consult-note/${patientID}/${noteID}`); break;
        case 'Follow-up Consult': navigate(`/follow-up-consult-note/${patientID}/${noteID}`); break;
        default: alert(`No dedicated editor for "${type}" notes yet.`); break;
    }
  };

  const exportNoteAsPDF = async (noteToExport) => {
    if (!noteToExport || !profile || !patientId) return alert("Missing data.");
    const patientRef = doc(db, 'patients', patientId);
    const patientSnap = await getDoc(patientRef);
    if (!patientSnap.exists()) return alert("Could not find patient data.");
    const patient = patientSnap.data();
    const patientFullName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();

    const docPDF = new jsPDF('p', 'pt', 'letter');
    const page = { width: docPDF.internal.pageSize.getWidth(), height: docPDF.internal.pageSize.getHeight() };
    const margin = 40;
    const contentWidth = page.width - (margin * 2);
    let cursorY = margin;

    const drawHeader = (title) => {
        const lumosyeRatio = 3226 / 825;
        const lumosyeWidth = 100;
        const lumosyeHeight = lumosyeWidth / lumosyeRatio;
        docPDF.addImage(lumosyeLogo, 'PNG', margin, cursorY, lumosyeWidth, lumosyeHeight);
        const lumochartSize = 40;
        docPDF.addImage(lumochartLogo, 'PNG', page.width - margin - lumochartSize, cursorY, lumochartSize, lumochartSize);
        cursorY += Math.max(lumosyeHeight, lumochartSize) + 15;
        docPDF.setDrawColor(220).line(margin, cursorY, page.width - margin, cursorY);
        cursorY += 20;
        docPDF.setFont("helvetica", "bold").setFontSize(20);
        docPDF.text(title, page.width / 2, cursorY, { align: 'center' });
        cursorY += 25;
        docPDF.setFont("helvetica", "normal").setFontSize(12);
        docPDF.text(`Patient: ${patientFullName}`, margin, cursorY);
        const noteDate = noteToExport.timestamp?.toDate ? noteToExport.timestamp.toDate() : new Date();
        const dateString = `Date: ${noteDate.toLocaleString()}`;
        const dateWidth = docPDF.getStringUnitWidth(dateString) * 12;
        docPDF.text(dateString, page.width - margin - dateWidth, cursorY);
        cursorY += 35;
    };

    const drawFooter = () => {
        const pageCount = docPDF.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            docPDF.setPage(i);
            docPDF.setFontSize(9).setTextColor(150);
            const pageNumText = `Page ${i} of ${pageCount} | LumoChart`;
            docPDF.text(pageNumText, page.width - margin, page.height - 20, { align: 'right' });
            docPDF.setFontSize(11).setFont("helvetica", "italic").setTextColor(100);
            const signatureText = `Signed electronically by ${profile.fullName}, ${profile.title}`;
            docPDF.text(signatureText, margin, page.height - 20);
        }
    };
    
    const drawTextSection = (title, content) => {
        if (!content || content.trim() === "") return;
        
        const addPageIfNeeded = (neededHeight) => {
            if (cursorY + neededHeight > page.height - (margin + 40)) { // Buffer for footer
                docPDF.addPage();
                cursorY = margin;
            }
        };

        addPageIfNeeded(30);
        docPDF.setFont("helvetica", "bold").setFontSize(14).setTextColor(0);
        docPDF.text(title, margin, cursorY);
        cursorY += 18;
        
        docPDF.setFont("helvetica", "normal").setFontSize(11);
        const contentLines = content.split('\n');
        
        contentLines.forEach(line => {
            const isBold = line.startsWith('**') && line.endsWith('**');
            const cleanLine = isBold ? line.substring(2, line.length - 2) : line;
            if (isBold) docPDF.setFont("helvetica", "bold");
            
            const splitLines = docPDF.splitTextToSize(cleanLine, contentWidth);
            splitLines.forEach(l => {
                addPageIfNeeded(12);
                docPDF.text(l, margin, cursorY);
                cursorY += 12;
            });
            
            if (isBold) docPDF.setFont("helvetica", "normal");
        });
        cursorY += 15;
    };
    
    const noteType = noteToExport.type || '';
    let pdfTitle = `${noteType} Note`;
    
    switch (noteType) {
        case 'Admission': pdfTitle = 'Admission Note'; break;
        case 'Progress': pdfTitle = 'Progress Note'; break;
        case 'Hospital Course': pdfTitle = 'Hospital Course Summary'; break;
        case 'Discharge': pdfTitle = 'Discharge Summary'; break;
        case 'Clinic Visit': pdfTitle = 'Clinic Visit Note'; break;
        case 'Clinic Follow-Up': pdfTitle = 'Follow-Up Visit Note'; break;
        case 'ED Note': pdfTitle = 'ED Note'; break;
        case 'Initial Consult': pdfTitle = 'Initial Consult Note'; break;
        case 'Follow-up Consult': pdfTitle = 'Follow-up Consult Note'; break;
        default: pdfTitle = `${noteType} Note`;
    }
    
    drawHeader(pdfTitle);

    if (['Hospital Course', 'Discharge'].includes(noteType)) {
        drawTextSection("Summary", noteToExport.summary);
    } else {
        let sectionsRendered = false;
        noteSectionOrder.forEach(({ key, title }) => {
            const content = noteToExport[key];
            if (content && key !== 'summary') {
                drawTextSection(title, content);
                sectionsRendered = true;
            }
        });
        if (!sectionsRendered && noteToExport.summary) {
            drawTextSection("Note Content", noteToExport.summary);
        }
    }

    drawFooter();
    const filename = `LumoChart_${pdfTitle.replace(/ /g, "_")}_${patient.lastName}.pdf`;
    docPDF.save(filename);
  };

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto py-10 px-4">
        <div className="flex items-center mb-8">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800 mr-4"><ArrowLeft size={20} /></button>
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">Past Notes</h1>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 mb-8">
            <FormInput type="text" placeholder="Search note content..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            <FormSelect value={noteTypeFilter} onChange={e => setNoteTypeFilter(e.target.value)}>
              <option value="">All Note Types</option>
              <option value="Admission">Admission</option>
              <option value="Progress">Progress</option>
              <option value="Hospital Course">Hospital Course</option>
              <option value="Discharge">Discharge</option>
              <option value="ED Note">ED Note</option>
              <option value="Initial Consult">Initial Consult</option>
              <option value="Follow-up Consult">Follow-up Consult</option>
              <option value="Clinic Visit">Clinic Visit</option>
              <option value="Clinic Follow-Up">Clinic Follow-Up</option>
            </FormSelect>
        </div>
        
        {allPatientNotes.length > 0 ? (
            <div className="space-y-4">
                {filteredNotes.map(note => (
                    <NoteCard
                        key={note.id}
                        note={note}
                        isExpanded={expandedNoteId === note.id}
                        onExpand={() => setExpandedNoteId(expandedNoteId === note.id ? null : note.id)}
                        onEdit={() => handleEditNote(note)}
                        onExport={() => exportNoteAsPDF(note)}
                        onMove={() => { setSelectedNoteId(note.id); setShowMoveModal(true); }}
                        onDelete={() => setNoteToDelete(note)}
                    />
                ))}
            </div>
        ) : (
            <div className="text-center p-10 bg-white dark:bg-zinc-900 rounded-2xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">No Notes Found</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-2">This patient does not have any notes yet.</p>
            </div>
        )}
      </main>

      {showMoveModal && selectedNoteId && (
        <MoveNoteModal
          patients={patients}
          currentPatientId={patientId}
          onMove={handleMoveNote}
          noteId={selectedNoteId}
          onClose={() => { setShowMoveModal(false); setSelectedNoteId(null); }}
        />
      )}

      {noteToDelete && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
          <div className="bg-white dark:bg-zinc-800 p-8 rounded-2xl shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/50">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">Delete Note</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Are you sure you want to delete this note? This action is permanent.</p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <button onClick={() => setNoteToDelete(null)} className="py-2.5 bg-slate-200 dark:bg-zinc-700 text-gray-800 dark:text-white rounded-lg font-semibold hover:bg-slate-300 dark:hover:bg-zinc-600 transition">Cancel</button>
              <button onClick={handleDeleteNote} className="py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}